import moment from 'moment';

import { getTask, getTaskGroupOpenTasks } from '../services/store-service';
import { createAlert } from '../services/alert-service';
import { resolveMilestones, getMilestoneType } from './milestone';
import { isTempId, cleanObject } from './helpers';
import { getEarliestDate, getLatestDate } from './date';

import { fetchTask, updateTask, moveTask, reorderTasks } from '../actions/tasks';

export const DEFAULT_TASK_DURATION = 1; // days
export const STORY_POINT_DURATION = 1; // days

export const resolveTasks = (taskIds, forcePromise, forceArray) => {
    let shouldFetch = false;

    const tasks = taskIds.filter(Boolean).map(task => task.id || task).map(taskId => {
        const task = getTask(taskId);

        if (!task) {
            shouldFetch = true;

            return fetchTask(taskId);
        }

        return task;
    });

    if (forceArray) {
        return tasks.filter(task => !(task instanceof Promise));
    }

    if (!shouldFetch) {
        if (forcePromise) {
            return Promise.resolve(tasks);
        }

        return tasks;
    } else {
        return Promise.all(tasks);
    }
}

export const moveTaskTo = async (source, destination, save) => {
    if (destination.taskGroupTaskProps) {
        updateTask(source.id, destination.taskGroupTaskProps, !save);
        return;
    }

    const shouldComplete = !source.completed && destination.completed;
    const shouldUncomplete = source.completed && !destination.completed && !destination.archived;
    const shouldArchive = !source.archived && destination.archived;
    const shouldUnarchive = source.archived && !destination.archived;

    const taskProps = {};
    const moveProps = {};

    if (source.order !== destination.order || source.taskGroupId !== destination.taskGroupId) {
        moveProps.order = destination.order;
        moveProps.task_group = destination.taskGroupId;
    }

    if ('milestoneId' in destination && source.milestoneId !== destination.milestoneId) {
        taskProps.milestone = destination.milestoneId || null;
    }

    if (shouldComplete) {
        taskProps.completed = true;
        taskProps.completed_on = new Date();
    }

    if (shouldUncomplete) {
        taskProps.completed = false;
        taskProps.completed_on = null;
    }

    if (shouldArchive) {
        taskProps.archived = true;
        taskProps.archived_on = new Date();
    }

    if (shouldUnarchive) {
        taskProps.archived = false;
        taskProps.archived_on = null;
    }

    if (save) {
        if ('completed' in taskProps || 'archived' in taskProps || 'milestone' in taskProps) {
            await updateTask(source.id, taskProps);
        }

        if ('order' in moveProps && moveProps.order !== undefined) {
            await moveTask(source.id, {
                type: 'task_group',
                id: moveProps.task_group,
                order: moveProps.order
            });
        }

        return;
    }

    updateTask(source.id, {...taskProps, ...moveProps}, true);
}

export const moveTasks = (taskGroupId, taskId, order, { archived, completed, completed_on, archived_on } = {}) => {
    const taskGroupTasks = getTaskGroupOpenTasks(taskGroupId);

    const newOrder = taskGroupTasks
        .filter(t => t.id !== taskId);

    newOrder.splice(order, 0, {
        id: taskId
    });

    // updateTask(taskId, {
    //     task_group: taskGroupId,
    //     archived,
    //     completed
    // }, true);

    reorderTasks(newOrder.map(({ id }, order) => ({
        id,
        order
    })), cleanObject({
        id: taskId,
        task_group: taskGroupId,
        archived,
        completed,
        order,
        completed_on,
        archived_on
    }));
}

const isMilestoneDependant = (task, withDetails) => {
    if (task && task.milestone) {
        const milestone = resolveMilestones([task.milestone], false, true)[0];

        if (!milestone || !milestone.dependency) {
            return false;
        }

        const milestoneDep = resolveMilestones([milestone.dependency], false, true)[0];

        if (!milestoneDep) {
            return false;
        }

        const isMilestoneDependant = Boolean(getMilestoneType(milestoneDep) !== 'completed');

        if (!withDetails) {
            return isMilestoneDependant;
        }

        if (isMilestoneDependant) {
            return {
                type: 'milestone',
                id: milestoneDep.id
            };
        }
    }

    return false;
}

const isTaskDepenendant = (task, withDetails) => {
    if (task && task.dependency) {
        const depTask = resolveTasks([task.dependency], false, true)[0];

        if (!depTask || depTask.completed) {
            return false;
        }

        if (!withDetails) {
            return true;
        }

        return {
            type: 'task',
            id: task.dependency
        };

    }

    return false;
}

export const isDependant = (taskId, withDetails) => {
    if (!taskId || isTempId(taskId)) {
        return false;
    }

    const task = resolveTasks([taskId], false, true)[0];

    return isMilestoneDependant(task, withDetails) || isTaskDepenendant(task, withDetails);
}

export const testTaskDueDateFilter = (rangeFilter, task) => {
    if (!rangeFilter || !task) {
        return false;
    }

    const taskStart = getTaskStart({
        startDate: task.start_date,
        dueDate: task.due_date,
        completedOn: task.completed_on
    });

    const taskEnd = getTaskEnd({
        startDate: task.start_date,
        dueDate: task.due_date,
        completedOn: task.completed_on
    });

    if (!taskStart || !taskEnd) {
        return false;
    }

    const rangeStart = rangeFilter.start ? new Date(rangeFilter.start) : 0;
    const rangeEnd = rangeFilter.end ? new Date(rangeFilter.end) : Number.POSITIVE_INFINITY;

    // Check if task start is in the range
    if (taskStart >= rangeStart && taskStart <= rangeEnd) {
        return true;
    }

    // Check if task end is in the range
    if (taskEnd >= rangeStart && taskEnd <= rangeEnd) {
        return true;
    }

    // Check if task is wrapping the range
    if (taskStart <= rangeStart && taskEnd >= rangeEnd) {
        return true;
    }

    return false;
}

const testTaskFieldFilter = (fieldFilter, task) => {
    if (!fieldFilter || !task) {
        return false;
    }

    const taskField = task.fields?.find(f => f.id === fieldFilter.id);

    if (!taskField) {
        return false;
    }

    if (fieldFilter.type === 'date') {
        return new Date(taskField.value) >= fieldFilter.value.start && new Date(taskField.value) <= fieldFilter.value.end;
    }

    return taskField.value == fieldFilter.value;
}

export const filterTask = (task, filter) => {
    if (!task) {
        return null;
    }

    const filterTerm = filter.term && filter.term.toLowerCase();

    if (
        filterTerm &&
        (task.name || '').toLowerCase().indexOf(filterTerm) === -1 &&
        (task.nice_id || '').toLowerCase().indexOf(filterTerm) === -1 &&
        (
            !task.fields ||
            task.fields.every(({ value }) => (value || '').toString().toLowerCase().indexOf(filterTerm) === -1)
        )
    ) {
        return null;
    }

    if (filter.project && (task.project !== filter.project)) {
        return null;
    }

    if (filter.user) {
        if (filter.user === 'unassigned') {
            if ((task.assignees || []).length) {
                return null;
            }
        } else if (!(task.assignees || []).includes(filter.user)) {
            return null;
        }
    }

    if (filter.milestone) {
        if (filter.milestone === 'no-milestone') {
            if (task.milestone) {
                return null;
            }
        } else if (task.milestone !== filter.milestone) {
            return null;
        }
    }

    if (filter.label && !(task.labels || []).includes(filter.label)) {
        return null;
    }

    if (filter.dueDate && !testTaskDueDateFilter(filter.dueDate, task)) {
        return null;
    }

    if (filter.field && !testTaskFieldFilter(filter.field, task)) {
        return null;
    }

    return task;
}

export const canChangeDueDate = (dueDate, milestone, showAlert) => {
    if (!dueDate) {
        return true;
    }

    if (!milestone) {
        return true;
    }

    if (new Date(milestone.start) > new Date(dueDate)) {
        if (showAlert) {
            createAlert('default', 'Task tied to milestone cannot end earlier than milestone start date');
        }

        return false;
    }

    if (moment(milestone.end).endOf('day').toDate() < new Date(dueDate)) {
        if (showAlert) {
            createAlert('default', 'Task tied to a milestone cannot end after the milestone end date.');
        }

        return false;
    }

    return true;
}

export const canChangeStartDate = (startDate, milestone, showAlert) => {
    if (!startDate) {
        return true;
    }

    if (!milestone) {
        return true;
    }

    if (new Date(milestone.end) < new Date(startDate)) {
        if (showAlert) {
            createAlert('default', 'Task tied to milestone cannot start after the milestone end date');
        }

        return false;
    }

    if (moment(milestone.start).startOf('day').toDate() > new Date(startDate)) {
        if (showAlert) {
            createAlert('default', 'Task tied to a milestone cannot start earlier than milestone start date.');
        }

        return false;
    }

    return true;
}

export const getTaskStart = ({ startDate, dueDate, completedOn, storyPoints, addDefaultDuration = true }) => {
    if (!dueDate && !startDate && !completedOn) {
        return null;
    }

    const endDate = completedOn || dueDate;

    if (startDate) {
        if (!endDate || new Date(startDate) <= new Date(endDate)) {
            return new Date(startDate).getTime();
        }
    }

    if (storyPoints) {
        return moment(endDate).add(- storyPoints * STORY_POINT_DURATION, 'days').startOf('day').valueOf();
    }

    if (addDefaultDuration) {
        return moment(endDate).add(- DEFAULT_TASK_DURATION, 'days').startOf('day').valueOf();
    } else {
        return moment(endDate).add(-15, 'minutes').valueOf();
    }
}

export const getTaskEnd = ({ startDate, dueDate, completedOn, storyPoints, addDefaultDuration = true }) => {
    if (!dueDate && !startDate && !completedOn) {
        return null;
    }

    const endDate = completedOn || dueDate;

    if (endDate) {
        return new Date(endDate).getTime();
    }

    if (storyPoints) {
        return moment(startDate).add(storyPoints * STORY_POINT_DURATION, 'days').startOf('day').valueOf();
    }

    if (addDefaultDuration) {
        return moment(startDate).add(DEFAULT_TASK_DURATION, 'days').startOf('day').valueOf();
    } else {
        return moment(startDate).startOf('day').valueOf();
    }
}

export const getTaskDuration = ({ startDate, dueDate, completedOn, storyPoints }) => {
    if (!dueDate && !startDate && !completedOn) {
        return 0;
    }

    const end = getTaskEnd({ startDate, dueDate, completedOn, storyPoints });
    const start = getTaskStart({ startDate, dueDate, completedOn, storyPoints });

    return moment(end).diff(moment(start), 'hours') / 24;
}

export const isPersonalTask = task => {
    return Boolean(!task?.task_group && !task?.task);
}

export const isSubtask = task => {
    return Boolean(task?.task);
}

export const getTasksStart = tasks => {
    const dates = tasks
        .map(task => task.start_date || task.due_date || null)
        .filter(Boolean)
        .map(date => new Date(date));

    if (!dates.length) {
        return new Date();
    }

    return getEarliestDate(dates);
}

export const getTasksEnd = tasks => {
    const dates = tasks
        .map(task => task.start_date || task.due_date || null)
        .filter(Boolean)
        .map(date => new Date(date));

    if (!dates.length) {
        return new Date();
    }

    return getLatestDate(dates);
}
